import React, { useState, useEffect } from "react";
import PageHeader from "../../components/PageHeader";
import { endPoints } from "../../constant/Environment";
import { deleteData, getAllData } from "../../Utility/API";
import { Button } from "react-bootstrap";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import { AiOutlineEdit } from "react-icons/ai";
import { Delete } from "@mui/icons-material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CreatePromoCampaign from "./CreatePromoCampaign";
import PromoUsageModal from "./PromoUsageModal";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;
  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, 0)} disabled={page === 0}>{theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}</IconButton>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0}>{theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}</IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1}>{theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}</IconButton>
      <IconButton onClick={(e) => onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))} disabled={page >= Math.ceil(count / rowsPerPage) - 1}>{theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}</IconButton>
    </Box>
  );
}

// Dates arrive as ISO-8601 with the server's offset — render them in the viewer's
// timezone. Legacy rows with no offset fall back to showing the stored wall clock.
const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v).replace("T", " ").slice(0, 16) : d.toLocaleString();
};

// Rewards are configured in USD; each player is credited the equivalent in their
// own currency (fiat or crypto) at redeem time.
const usd = (v) => `$${Number(v || 0).toFixed(2)}`;

const rewardText = (row) => {
  const cash = usd(row.cashRewardUsd);
  const bonus = usd(row.bonusRewardUsd);
  switch (row.rewardType) {
    case "CASH": return `${cash} cash`;
    case "BONUS": return `${bonus} bonus`;
    case "BOTH":
    case "WELCOME":
    case "REFERRAL": return `${cash} cash + ${bonus} bonus`;
    case "BONUS_PERCENT": return `${Number(row.bonusPercentage || 0)}% on purchase`;
    case "REFERRAL_BOOST": return `+${Number(row.bonusPercentage || 0)}% on next referral`;
    case "FREE_SPINS": return `${Number(row.freeSpinCount || 0)} free spins on ${row.freeSpinGameName || row.freeSpinGameId || "-"}`;
    default: return row.rewardType || "-";
  }
};

// The purchase gate is what separates a "free for all" drop from a "purchase
// required" one, so it belongs in the list rather than only inside the edit form.
const purchaseText = (row) => {
  const min = Number(row.minPurchaseAmount || 0);
  if (!(min > 0)) return "None";
  return `${usd(min)} / ${Number(row.purchasePeriodDays) || 3}d`;
};

const PromoCampaign = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState({});
  const [usagePromo, setUsagePromo] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    try {
      const response = await getAllData(`${endPoints.api.PROMO_CAMPAIGN_ALL}`);
      if (response.status === "success") setRows(response.data || []);
      else ErrorToast("Failed to fetch promotions");
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCampaign = (id) => {
    if (!window.confirm("Delete this promotion?")) return;
    setIsLoading(true);
    deleteData(`${endPoints.api.PROMO_CAMPAIGN_DELETE}/${id}`)
      .then((response) => {
        setIsLoading(false);
        if (response.data.status === "success") {
          SuccesToast("Promotion deleted");
          fetchCampaigns();
        } else ErrorToast(response.data.message);
      })
      .catch((err) => { console.error(err); setIsLoading(false); });
  };

  const onClose = () => { setShowForm(false); fetchCampaigns(); };

  return (
    <div className="main">
      <Toaster />
      {isLoading && <Loader spinner={true} visible={isLoading} />}
      <div id="Sportbook" style={{ padding: "0px" }}>
        <div className="container-fluid bg-color-sportbook custom-card4">
          <div className="inner-space1">
            <div className="row">
              <div className="col-md-12" style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #444" }}>
                <PageHeader title="Promo Codes" crumb="Promotions / Promo Codes" />
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <Button variant="primary"
                    style={{ height: "fit-content", fontWeight: 600, padding: "8px 20px", borderRadius: 4, marginBottom: 12 }}
                    onClick={() => { setEditData({}); setShowForm(true); }}>
                    Add Promo
                  </Button>
                </div>
              </div>
            </div>

            <CreatePromoCampaign open={showForm} onClose={onClose} editData={editData} />
            <PromoUsageModal promo={usagePromo} onClose={() => setUsagePromo(null)} />

            <div className="container-fluid table-custom">
              <div className="row" style={{ padding: "5px 0px 20px 0px" }}>
                <div className="col-md-12">
                  <TableContainer component={Paper}>
                    <Table className="c_table" size="small" stickyHeader sx={{ minWidth: 500 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">Code</TableCell>
                          <TableCell align="center">Title</TableCell>
                          <TableCell align="center">Type</TableCell>
                          <TableCell align="center">Reward (USD)</TableCell>
                          <TableCell align="center">Purchase Req.</TableCell>
                          <TableCell align="center">Per User</TableCell>
                          <TableCell align="center">Total</TableCell>
                          <TableCell align="center">Redeemed</TableCell>
                          <TableCell align="center">From</TableCell>
                          <TableCell align="center">To</TableCell>
                          <TableCell align="center">Active</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows).map((row, i) => (
                          <TableRow key={row.id || i}>
                            <TableCell align="center">{row.promoCode}</TableCell>
                            <TableCell align="center">{row.title || "-"}</TableCell>
                            <TableCell align="center">{row.rewardType}</TableCell>
                            <TableCell align="center">{rewardText(row)}</TableCell>
                            <TableCell align="center">{purchaseText(row)}</TableCell>
                            <TableCell align="center">{row.perUserLimit ?? "-"}</TableCell>
                            <TableCell align="center">{Number(row.totalLimit) > 0 ? row.totalLimit : "∞"}</TableCell>
                            <TableCell align="center">{row.totalRedeemed ?? 0}</TableCell>
                            <TableCell align="center">{fmtDate(row.startDate)}</TableCell>
                            <TableCell align="center">{fmtDate(row.endDate)}</TableCell>
                            <TableCell align="center">{row.active ? "On" : "Off"}</TableCell>
                            <TableCell align="center">
                              <div style={{ display: "flex", gap: 10, justifyContent: "center", alignItems: "center" }}>
                                <AiOutlineEdit size={20} title="Edit" style={{ cursor: "pointer" }}
                                  onClick={() => { setEditData(row); setShowForm(true); }} />
                                <span onClick={() => deleteCampaign(row.id)} title="Delete" style={{ cursor: "pointer" }}><Delete /></span>
                                <span onClick={() => setUsagePromo(row)} title="Redemptions" style={{ cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>Log</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {emptyRows > 0 && (<TableRow style={{ height: 53 * emptyRows }}><TableCell colSpan={12} /></TableRow>)}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={[10, 25, 50, { label: "All", value: -1 }]}
                            colSpan={12}
                            count={rows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
                            onPageChange={(e, p) => setPage(p)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            ActionsComponent={TablePaginationActions}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCampaign;
